import {
  FieldKind,
  GraphQlEnum,
  GraphQlField,
  GraphQlSchema,
  GraphQlType,
  TypeKind
} from '../../types/GraphQlSchema';

/**
 * Parses the response from the schema introspection query and builds
 * {@link GraphQlSchema} object from it.
 *
 * @param gqlSchema to be parsed
 * @returns {GraphQlSchema} object from the parsed input
 */
export function parseGqlSchema(gqlSchema: any): GraphQlSchema {
  const typesArray: Array<any> = gqlSchema.data['__schema'].types;

  const schema: GraphQlSchema = {
    objectTypes: new Map<string, GraphQlType>(),
    inputTypes: new Map<string, GraphQlType>(),
    scalars: new Map<string, GraphQlType>(),
    enums: new Map<string, GraphQlEnum>()
  };

  typesArray.forEach(type => {
    const kind = TypeKind[type.kind as keyof typeof TypeKind];

    if (TypeKind.ENUM === kind) {
      const enumName = type.name;
      schema.enums.set(enumName, {name: enumName, values: type.enumValues?.map((value: any) => value.name)});
    } else {
      const gqlType = resolveType(type, kind, schema);

      // TODO make it better, this is ugly
      if ([TypeKind.OBJECT, TypeKind.INTERFACE].includes(gqlType.kind)) {
        schema.objectTypes.set(gqlType.name, gqlType);
      } else if (TypeKind.INPUT_OBJECT === gqlType.kind) {
        schema.inputTypes.set(gqlType.name, gqlType);
      } else if (TypeKind.SCALAR === gqlType.kind) {
        schema.scalars.set(gqlType.name, gqlType);
      }
    }
  });

  return schema;
}

function resolveType(type: any, kind: TypeKind, schema: GraphQlSchema): GraphQlType {
  const fields = resolveFields(type.fields);
  return {
    name: type.name,
    kind,
    fields: fields,
    fieldsMap: resolveFieldsMap(fields),
    schemaRef: schema
  };
}

/**
 * Resolves the {@link GraphQlField}s from the introspection response.
 *
 * @param {Array} fields to be resolved
 * @returns {Array} of the resolved graphql fields objects
 */
function resolveFields(fields?: Array<any>): Array<GraphQlField> {
  if (!fields) {
    return [];
  }
  return fields.map((field: any) => {
    const type = field.type;
    return {
      name: field.name,
      kind: resolveFieldKind(type),
      range: resolveRange(type),
      isMulti: isMultivaluedField(type),
      arguments: resolveArguments(field)
    }
  });
}

function resolveFieldsMap(fields: Array<GraphQlField>): Map<string, GraphQlField> {
  const fieldsMap = new Map<string, GraphQlField>();
  fields.forEach(field => {
    fieldsMap.set(field.name, field);
  });
  return fieldsMap;
}

/**
 * Resolves the {@link FieldKind} from the field type. The logic traverses
 * the object in dept via recursion until it finds the actual type, because
 * it could be wrapped by `NOT_NULL` or `LIST` types.
 *
 * @param type to be used for the resolving
 * @returns {FieldKind} representing the actual unwrapped type of the field
 */
function resolveFieldKind(type: any): FieldKind {
  if (Object.values(FieldKind).includes(type.kind) && type.name) {
    return FieldKind[type.kind as keyof typeof FieldKind];
  }
  return resolveFieldKind(type.ofType);
}

/**
 * Resolves the `range` of the field, which could be: `ID`, `string`,
 * `Literal`, `positiveInteger`, etc. If the type is wrapped, the logic
 * will travers the types util it finds an non-nullable range.
 *
 * @param type to be used for resolving
 * @returns {string} the range of the field
 */
function resolveRange(type: any): string | undefined {
  if (!type) {
    return undefined;
  }
  if (type.name) {
    return type.name;
  }
  return resolveRange(type.ofType);
}

/**
 * Resolves whether the field is multivalued or not. The logic checks, whether
 * the first or the second type is `LIST`.
 *
 * @param type to be used for resolving
 * @returns {boolean} true when the field is multivalued, false otherwise
 */
function isMultivaluedField(type: any): boolean {
  if (type.kind === 'NOT_NULL') {
    return isMultivaluedField(type.ofType);
  }
  return type.kind === 'LIST';
}

/**
 * Resolves the input arguments of the field.
 *
 * @param field the field with arguments to resolve
 * @returns array of arguments names
 */
function resolveArguments(field: any): Array<string> {
  return (field.args || []).map((argument: any) => argument.name);
}
